import { ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, Spin } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageTitle from 'components/page-title';
import TabbedPage from 'components/tabbed-page';
import { EmbeddedContext } from 'components/tabbed-page/embedded';
import { useCurrentRoles } from 'hooks/use-current-roles';
import { formatDay } from 'pages/data-import/utils';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import SummaryTiles from './components/summary-tiles';
import { DataIssuesContext, DataIssuesContextValue } from './context';
import DepartedTab from './tabs/departed-tab';
import MissingTab from './tabs/missing-tab';
import RenamesTab from './tabs/renames-tab';
import UnmatchedTab from './tabs/unmatched-tab';
import { IssueSummary, IssueTabKey } from './types';
import {
  DEFAULT_TAB,
  ISSUE_TABS,
  NO_FILE_TEXT,
  activeTabOf,
  issuesErrorMessage,
  normalizeSummary,
} from './utils';
import styles from './style.module.scss';

const TITLE = 'Vấn đề dữ liệu';

const TAB_VIEWS: Record<IssueTabKey, () => JSX.Element> = {
  'roi-di': DepartedTab,
  'vang-mat': MissingTab,
  'doi-id': RenamesTab,
  'chua-co-tai-khoan': UnmatchedTab,
};

type SummaryState =
  | { status: 'loading'; data: IssueSummary | null }
  | { status: 'ready'; data: IssueSummary }
  | { status: 'failed'; data: null; message: string };

/**
 * Doanh thu → Vấn đề dữ liệu (docs/nhap-du-lieu/DAC-TA-DOT-2-VAN-DE-DU-LIEU-2026-09-29.md, mục 4).
 * Trang chỉ xem, ADMIN và MOD đều vào được: Creator đã rời đại lý, vắng khỏi file, đổi TikTok ID
 * và UID chưa có tài khoản. Số liệu lấy từ các file đã nhập ở trang Nhập dữ liệu.
 */
const DataIssuesPage = () => {
  const { isAdmin, canImportData } = useCurrentRoles();
  const [params, setParams] = useSearchParams();
  const [refreshKey, setRefreshKey] = useState(0);
  const [summary, setSummary] = useState<SummaryState>({ status: 'loading', data: null });
  const tabsRef = useRef<HTMLDivElement>(null);

  const reload = useCallback(() => setRefreshKey((key) => key + 1), []);

  useEffect(() => {
    let cancelled = false;
    // Đang tải lại: giữ số cũ trên màn hình cho tới khi có số mới.
    setSummary((prev) => ({ status: 'loading', data: prev.data }));
    AxiosUtils.get(`${API.dataIssues}/summary`)
      .then((res: any) => {
        if (!cancelled) setSummary({ status: 'ready', data: normalizeSummary(res?.data) });
      })
      .catch((err) => {
        if (!cancelled) {
          setSummary({ status: 'failed', data: null, message: issuesErrorMessage(err) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const active = activeTabOf(params.get('tab'));

  const openTab = (key: IssueTabKey) => {
    // Cùng cách ghi URL với TabbedPage: tab mặc định không cần `?tab=`.
    const next = new URLSearchParams();
    if (key !== DEFAULT_TAB) next.set('tab', key);
    setParams(next, { replace: true });
    // Điện thoại: bốn ô chiếm gần hết màn hình, cuộn tới bảng vừa mở.
    const node = tabsRef.current;
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const hasData = summary.status === 'ready' ? !!summary.data.latestPeriodEnd : null;

  const ctx: DataIssuesContextValue = useMemo(
    () => ({ isAdmin, hasData, refreshKey, reload }),
    [isAdmin, hasData, refreshKey, reload]
  );

  return (
    <DataIssuesContext.Provider value={ctx}>
      <PageTitle title={TITLE} />

      {summary.status === 'failed' ? (
        <Alert
          className={styles.section}
          type="error"
          showIcon
          message="Chưa tải được số tóm tắt"
          // Nút nằm dưới lời báo lỗi: đặt bên phải thì ở điện thoại lời báo lỗi bị ép hẹp.
          description={
            <div className={styles.alertBody}>
              <span>{summary.message}</span>
              <Button size="small" icon={<ReloadOutlined />} onClick={reload}>
                Tải lại
              </Button>
            </div>
          }
        />
      ) : (
        // Thẻ div chứ không phải p: vòng chờ của antd là một thẻ div.
        <div className={styles.asOf} aria-live="polite">
          {!summary.data ? (
            <>
              <Spin size="small" /> Đang tải số tóm tắt…
            </>
          ) : summary.data.latestPeriodEnd ? (
            <span>
              Tính đến dữ liệu ngày{' '}
              <strong className={styles.asOfDay}>{formatDay(summary.data.latestPeriodEnd)}</strong>
            </span>
          ) : (
            <>
              <strong className={styles.asOfDay}>{NO_FILE_TEXT}</strong>
              {canImportData && <Link to="/data-import">Mở trang Nhập dữ liệu</Link>}
            </>
          )}
        </div>
      )}

      <SummaryTiles summary={summary.data} active={active} onOpen={openTab} />

      <div ref={tabsRef}>
        {/* TabbedPage tự vẽ tiêu đề trang ngay trên dải tab; ở trang này tiêu đề đã nằm phía
            trên bốn ô số, nên báo cho nó biết đang "nằm trong trang khác" để không vẽ lần hai. */}
        <EmbeddedContext.Provider value>
          <TabbedPage
            title={TITLE}
            tabs={ISSUE_TABS.map((tab) => {
              const View = TAB_VIEWS[tab.key];
              return { key: tab.key, label: tab.label, render: () => <View /> };
            })}
          />
        </EmbeddedContext.Provider>
      </div>
    </DataIssuesContext.Provider>
  );
};

export default DataIssuesPage;
